<template>
    <div :class="useTransitions ? 'with-transitions' : 'without-transitions'" class="page-component-container">
        <transition :name="useTransitions ? componentTransition : null">
            <breadcrumb :to="breadcrumbUrl" v-if="showBreadcrumb">{{ currentCase.title }}</breadcrumb>
        </transition>

        <transition :name="useTransitions ? componentTransition : null">
            <div :key="key" class="components-container components-full row" v-if="showResults">
                <div class="col-sm-12">
                    <page-component
                            :description="description"
                            :options="resultOptions"
                            :title="title"
                            component="Results"
                            side="full"
                            v-on:goBack="onGoBack"
                            v-on:pickChoice="onPickResultChoice"
                    />
                </div>
            </div>
            <div :key="key"
                 class="components-container components-side-by-side row" v-else-if="!forceFull && (halfWidth || hasTwoComponents)">
                <div class="col-sm-6">
                    <page-component
                            :component="leftComponent"
                            :description="description"
                            :options="leftComponentOptions"
                            :title="title"
                            side="left"
                            v-on:continue="onContinue"
                            v-on:end="onEnd"
                            v-on:goBack="onGoBack"
                            v-on:pickChoice="onPickResultChoice"
                    />
                </div>
                <div class="col-sm-6" v-if="hasTwoComponents">
                    <page-component
                            :component="rightComponent"
                            :description="description"
                            :options="rightComponentOptions"
                            :title="title"
                            side="right"
                            v-on:continue="onContinue"
                            v-on:end="onEnd"
                            v-on:goBack="onGoBack"
                            v-on:pickChoice="onPickResultChoice"
                    />
                </div>
            </div>
            <div :key="key" class="components-container components-full row" v-else>
                <div class="col-sm-12">
                    <page-component
                            :component="leftComponent"
                            :description="description"
                            :options="leftComponentOptions"
                            :title="title"
                            side="full"
                            v-on:continue="onContinue"
                            v-on:end="onEnd"
                            v-on:goBack="onGoBack"
                            v-on:pickChoice="onPickResultChoice"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>
<script>
    import {mapGetters} from 'vuex'
    import PageComponent from './page-component'
    import PagePhase from '@/enum/PagePhase'
    import GamePhase from '@/enum/GamePhase'
    import AppConfig from '@/app-config'
    import Breadcrumb from '@/components/breadcrumb'
    import * as MutationTypes from '@/store/mutation-types'
    import GameType from '@/enum/GameType'

    export default {
        name: 'page-component-container',
        props: {
            breadcrumbUrl: {
                type: String,
                required: false
            },
            title: {
                type: String,
                required: false
            },
            description: {
                type: String,
                required: false
            },
            leftComponent: {
                type: String,
                required: true
            },
            leftComponentOptions: {
                type: Object,
                required: false,
                default() {
                    return {}
                }
            },
            rightComponent: {
                type: String,
                required: false
            },
            rightComponentOptions: {
                type: Object,
                required: false,
                default() {
                    return {}
                }
            },
            useTransitions: {
                type: Boolean,
                required: false,
                default: true
            }
        },
        components: {
            PageComponent,
            Breadcrumb
        },
        data() {
            return {
                componentTransition: AppConfig.componentTransition
            }
        },
        computed: {
            ...mapGetters({
                currentCase: 'getCurrentCase',
                actualCurrentPageIndex: 'getCurrentPageIndex',
                currentPagePhase: 'getCurrentPagePhase',
                pageHistory: 'getPageHistory',
                gameType: 'getGameType',
            }),

            forceFull() {
                return this.isSinglePlayer;
            },
            isSinglePlayer() {
                return this.gameType === GameType.SinglePlayer
            },

            hasTwoComponents() {
                return this.leftComponent && this.rightComponent
            },

            halfWidth() {
                return this.leftComponentSupport.maxSize === 'half'
            },

            leftComponentSupport() {
                if (!this.leftComponent) {
                    return null
                }

                if (!AppConfig.componentSupport.hasOwnProperty(this.leftComponent)) {
                    return null
                }

                return AppConfig.componentSupport[this.leftComponent]
            },

            rightComponentSupport() {
                if (!this.rightComponent) {
                    return null
                }

                if (!AppConfig.componentSupport.hasOwnProperty(this.rightComponent)) {
                    return null
                }

                return AppConfig.componentSupport[this.rightComponent]
            },

            key() {
                // This allows the transition to figure out that it needs to create a new instance of the object and fade between them
                return this.actualCurrentPageIndex + '-' + this.currentPagePhase
            },

            useResult() {
                return !this.isSinglePlayer && (this.leftComponent === 'Choices' || this.rightComponent === 'Choices')
            },

            showResults() {
                return this.currentPagePhase === PagePhase.Result
            },

            resultOptions() {
                if (this.leftComponent === 'Choices') {
                    return this.leftComponentOptions
                } else if (this.rightComponent === 'Choices') {
                    return this.rightComponentOptions
                } else {
                    throw new Error(`Could not find choice component on left side (${this.leftComponent}) or right side (${this.rightComponent})`)
                }
            },

            showBreadcrumb() {
                return this.breadcrumbUrl && this.currentCase
            },
        },
        methods: {
            onPickResultChoice(choice) {
                console.log('page-component-container.onPickResultChoice', choice);

                this.onContinue(choice && choice.hasOwnProperty('gotoPageIndex') ? choice.gotoPageIndex : null)
            },
            onGoBack(backButton) {
                let gotoPageIndex = null;
                let backButtonStatic = false;

                if (typeof backButton === 'boolean') {
                    let currentPageIndex = this.actualCurrentPageIndex
                    let pageHistory = this.pageHistory

                    for (let i = pageHistory.length - 1; i >= 0; i--) {
                        let pageHistoryIndex = pageHistory[i]

                        if (pageHistoryIndex !== currentPageIndex) {
                            gotoPageIndex = pageHistoryIndex

                            break
                        }
                    }
                } else if (backButton) {
                    gotoPageIndex = backButton;

                    backButtonStatic = true;
                }

                if (gotoPageIndex) {
                    if (backButtonStatic) {
                        let pagePhase = PagePhase.Normal;

                        console.log(`Going back to page index ${gotoPageIndex} (phase: ${pagePhase})`)

                        this.$gameClient.send({
                            _pagePhase: pagePhase,
                            _currentPageIndex: gotoPageIndex
                        })
                    } else {
                        let pagePhase = this.isSinglePlayer ? PagePhase.Normal : PagePhase.Result;

                        console.log(`Going back to page index ${gotoPageIndex} (phase: ${pagePhase})`)

                        this.$gameClient.send({
                            _pagePhase: pagePhase,
                            _currentPageIndex: gotoPageIndex
                        })
                    }
                } else {
                    console.log('Could not go back as there was no back page index')
                }
            },
            onEnd() {
                this.$store.commit(MutationTypes.SET_NAVIGATION_GUARD_ENABLED, false)

                this.$gameClient.send({
                    _phase: GamePhase.End
                })
            },
            onContinue(gotoPageIndex = null) {
                console.log('page-component-container.onContinue', gotoPageIndex);
                if (this.useResult && this.currentPagePhase !== PagePhase.Result) {
                    this.$gameClient.send({
                        _pagePhase: PagePhase.Result
                    })
                } else {
                    let currentPageIndex = this.actualCurrentPageIndex

                    if (gotoPageIndex === null) {
                        if (this.leftComponentOptions) {
                            if (this.leftComponentOptions.hasOwnProperty('gotoPageIndex')) {
                                gotoPageIndex = this.leftComponentOptions.gotoPageIndex
                            } else if (this.leftComponentOptions.hasOwnProperty('gotoPageId')) {
                                gotoPageIndex = this.leftComponentOptions.gotoPageId - 1
                            }
                        }
                    }

                    if (gotoPageIndex === null || gotoPageIndex === currentPageIndex) {
                        gotoPageIndex = currentPageIndex + 1
                    }

                    this.$gameClient.send({
                        _pagePhase: PagePhase.Normal,
                        _currentPageIndex: gotoPageIndex
                    })
                }
            }
        }
    }
</script>

<style lang="less">
    .page-component-container.with-transitions {
        .components-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
        }
    }

    .components-container {
        margin-left: 0 !important;
    }
</style>